import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import { useRegionFilter, useSetCityFilter } from 'hooks/form/search';
import { QaIdentifier, useQa } from 'hooks/useQa';

import { useFilteredList } from '../__hooks/useFilteredList';
import { useLocationFilter } from '../__hooks/useLocationFilter';
import styles from '../Location.module.scss';
import { LocationFilterType, RegionList } from '../types';

import Input from './Input';
import Overlay from './Overlay';
import SimpleList from './SimpleList';

interface RegionFilterProps {
    regionList: RegionList;
}

const RegionFilter = ({ regionList }: RegionFilterProps) => {
    const { t } = useTranslation();
    const [regionIdList, setRegionIdList] = useRegionFilter();
    const setCityIdList = useSetCityFilter();
    const {
        isOpen,
        inputValue,
        currentSelection,
        isSelectionEmpty,
        overlayRef,
        setInputValue,
        onSelection,
        clearSelection,
        onSubmit,
        openOverlay,
    } = useLocationFilter(regionList, regionIdList, setRegionIdList);
    const filteredList = useFilteredList(
        regionList,
        inputValue,
        currentSelection
    );
    const qaAttr = useQa(QaIdentifier.Search_Filter_Location_Region);

    const onSubmit_ = useCallback(() => {
        if (currentSelection.size > 0) {
            //on active region selection, clear all city selections not contained in selected regions
            setCityIdList((cityIdList) => {
                const newCityIdList = new Set([...cityIdList]);
                regionList
                    .filter((region) => !currentSelection.has(region.id))
                    .forEach((region) => {
                        region.cityList.forEach((city) => {
                            newCityIdList.delete(city.id);
                        });
                    });

                return newCityIdList;
            });
        }
        onSubmit();
    }, [currentSelection, onSubmit, regionList, setCityIdList]);

    return (
        <div
            className={classNames(styles.Location, { [styles.Open]: isOpen })}
            ref={overlayRef}
            {...qaAttr}
        >
            <div>
                <Input
                    type={LocationFilterType.Region}
                    placeholder={t('TRANS-all-regions')}
                    onOpen={openOverlay}
                    value={inputValue}
                    onValueChange={setInputValue}
                />
                {isOpen && (
                    <Overlay
                        type={LocationFilterType.Region}
                        isSelectionEmpty={isSelectionEmpty}
                        onClearSelection={clearSelection}
                        onSubmit={onSubmit_}
                    >
                        <SimpleList
                            list={filteredList}
                            type={LocationFilterType.Region}
                            onSelection={onSelection}
                            currentSelection={currentSelection}
                        />
                    </Overlay>
                )}
            </div>
        </div>
    );
};

export default RegionFilter;
